import { useEffect, useState } from 'react';
import { X, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import { MarketplaceFilters as FiltersType, MarketplaceCategory } from '../../types/marketplace';

interface MarketplaceFiltersProps {
  filters: FiltersType;
  categories: MarketplaceCategory[];
  onChange: (filters: FiltersType) => void;
  className?: string;
}

export const MarketplaceFilters= ({
  filters,
  categories,
  onChange,
  className = ''
}: MarketplaceFiltersProps) => {
  const [showPriceFilter, setShowPriceFilter] = useState(false);
  const [tempPriceRange, setTempPriceRange] = useState({
    min: filters.priceRange?.min || 0,
    max: filters.priceRange?.max === Infinity ? 1000 : (filters.priceRange?.max || 1000)
  });

  const sortOptions = [
    { value: 'popularity', label: '📊 Most Popular' },
    { value: 'rating', label: '⭐ Highest Rated' },
    { value: 'newest', label: '🆕 Newest First' },
    { value: 'price_low', label: '💰 Price: Low to High' },
    { value: 'price_high', label: '💰 Price: High to Low' }
  ];

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'workflow', label: '🔄 Workflows' },
    { value: 'node', label: '⚙️ Custom Nodes' },
    { value: 'template', label: '📋 Templates' }
  ];

  const ratingOptions = [
    { value: 0, label: 'All Ratings' },
    { value: 4, label: '⭐⭐⭐⭐ & Up' },
    { value: 3, label: '⭐⭐⭐ & Up' },
    { value: 2, label: '⭐⭐ & Up' }
  ];

  const handleFilterChange = (key: keyof FiltersType, value: any) => {
    onChange({
      ...filters,
      [key]: value
    });
  };

  const handlePriceRangeApply = () => {
    handleFilterChange('priceRange', {
      min: tempPriceRange.min,
      max: tempPriceRange.max === 1000 ? Infinity : tempPriceRange.max
    });
    setShowPriceFilter(false);
  };

  const handleTagRemove = (tagToRemove: string) => {
    const newTags = (filters.tags || []).filter(tag => tag !== tagToRemove);
    handleFilterChange('tags', newTags);
  };

  const clearAllFilters = () => {
    onChange({
      category: '',
      tags: [],
      priceRange: { min: 0, max: Infinity },
      minRating: 0,
      type: '',
      sortBy: 'popularity'
    });
    setTempPriceRange({ min: 0, max: 1000 });
  };

  const hasActiveFilters = () => {
    return filters.category || 
           (filters.tags && filters.tags.length > 0) ||
           filters.minRating ||
           filters.type ||
           (filters.priceRange && (filters.priceRange.min > 0 || filters.priceRange.max < Infinity));
  };
  return (
    <div className={` ${className}`}>
      <div className="p-4">
        <div className="row">
          <div className='col-auto'>
            <h3 className="text-muted mb-0">Filters</h3>
          </div>
          <div className='col-auto'>
            {hasActiveFilters() && (
            <button
              onClick={clearAllFilters}
              className="form-control p-0"
              title='Clear all'
            >
              <X className="w-4 h-4 text-danger" />
            </button>
          )}
          </div>
        </div>
        <div className="row mb-2">
          {/* Sort By */}
          <div className='col-3'>
            <label className="text-muted mb-0">
              Sort By
            </label>
            <select
              value={filters.sortBy || 'popularity'}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="form-select"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {/* Type Filter */}
          <div className='col-3'>
            <div>
              <label className="text-muted mb-0">
                Content Type
              </label>
              <select
                value={filters.type || ''}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="form-select"
              >
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Rating Filter */}
          <div className='col-3'>
            <div>
              <label className="text-muted mb-0">
                Minimum Rating
              </label>
              <select
                value={filters.minRating || 0}
                onChange={(e) => handleFilterChange('minRating', parseInt(e.target.value))}
                className="form-select"
              >
                {ratingOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Quick Filters */}
          <div className='col-3'>
            <div>
              <label className="text-muted mb-0">
                Quick Filters
              </label>
              <div className="input-group">
                <button
                  onClick={() => handleFilterChange('priceRange', { min: 0, max: 0 })}
                  className={`form-control ${
                    filters.priceRange?.min === 0 && filters.priceRange?.max === 0
                      ? 'btn-primary'
                      : 'btn-outline-secondary'
                  }`}
                >
                  🆓 Free Only
                </button>
                <button
                  onClick={() => handleFilterChange('minRating', 4)}
                  className={`form-control ${
                    filters.minRating === 4
                      ? 'btn-primary'
                      : 'btn-outline-secondary'
                  }`}
                >
                  ⭐ High Quality (4+ stars)
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className='row mb-2'>
        {/* Price Range */}
          <div className='col-3'>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="text-muted mb-0">
                  Price Range
                </label>
                <button
                  onClick={() => setShowPriceFilter(!showPriceFilter)}
                  className="form-select w-auto"
                >
                  <DollarSign className="w-2 h-2" />
                  {showPriceFilter ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
              
              {showPriceFilter && (
                <div className="row mb-2">
                  <div className='col-12 mb-1'>
                    <div className="input-group">
                      <input
                        type="number"
                        min="0"
                        value={tempPriceRange.min}
                        onChange={(e) => setTempPriceRange({
                          ...tempPriceRange,
                          min: Math.max(0, parseInt(e.target.value) || 0)
                        })}
                        className="form-control"
                        placeholder="Min"
                      />
                      <span className="input-group-text">to</span>
                      <input
                        type="number"
                        min="0"
                        value={tempPriceRange.max}
                        onChange={(e) => setTempPriceRange({
                          ...tempPriceRange,
                          max: Math.max(tempPriceRange.min, parseInt(e.target.value) || 0)
                        })}
                        className="form-control"
                        placeholder="Max"
                      />
                    </div>
                  </div>
                  <div className="row p-0 m-0">
                    <div className='col-6'>
                      <button
                        onClick={() => {
                          setTempPriceRange({ min: 0, max: 1000 });
                          setShowPriceFilter(false);
                        }}
                        className="form-control"
                      >
                        Cancel
                      </button>
                    </div>
                    <div className='col-6'>
                      <button
                        onClick={handlePriceRangeApply}
                        className="form-control"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Active Tags */}
          {filters.tags && filters.tags.length > 0 && (
          <div className='col-3'>
              <div className="mb-6">
                <label className="text-muted mb-0">
                  Active Tags
                </label>
                <div className="input-group">
                  {filters.tags.map((tag) => (
                    <span
                      key={tag}
                      className="input-group-text"
                    >
                      #{tag}
                      <button
                        onClick={() => handleTagRemove(tag)}
                        className="form-control"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
          </div>
          )}
          {/* Categories */}
          <div className='col-auto'>
          {categories.length > 0 && (
            <div>
              <label className="text-muted mb-0">
                Category
              </label>
              <div className="space-y-2">
                <button
                  onClick={() => handleFilterChange('category', '')}
                  className={`btn ${
                    !filters.category
                      ? 'btn-primary'
                      : 'btn-outline-secondary'
                  }`}
                >
                  All Categories
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleFilterChange('category', category.name)}
                    title={category.name}
                    className={`btn ms-1 ${
                      filters.category === category.name ? 'btn-primary' : 'btn-outline-secondary'
                    }`}
                  >
                    <span className="flex items-center">
                      <span>{category.icon}</span>
                      <span>{category.name}</span>
                    </span>
                    {category.itemCount !== undefined && (
                      <span>
                        {category.itemCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};